import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { getDashboardPanelTokens, rem } from '../dashboardPanelTokens';
import { getToneTokens } from './coverageTokens';
import { money } from './coverageFormat';
import { TilePrimaryButton } from './CoverageActions';
import type { PaymentDue } from './paymentDue';
import { TileHeading } from '../TileHeading';

interface PaymentDuePanelProps {
  readonly payment: PaymentDue;
}

/**
 * What is owed, and a way to pay it.
 *
 * The dashboard previously said only "Pay -- your policy is ready, please make
 * payment", with no amount anywhere on the page. The figure is the whole point of
 * the prompt, so it leads.
 *
 * No due date and no countdown: the policy read carries neither, and inventing one
 * on the tile whose job is urgency is the last place to guess.
 */
export function PaymentDuePanel({ payment }: Readonly<PaymentDuePanelProps>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);
  const caution = getToneTokens('caution', palette.mode);
  const paymentProcessor = useSelector(
    ({ account }: any) => account?.paymentProcessor
  );

  return (
    <Box data-qa="PAYMENT_DUE_PANEL" sx={{ width: '100%', minWidth: 0 }}>
      <Box
        display="flex"
        alignItems="baseline"
        justifyContent="space-between"
        gap={rem(12)}
      >
        <TileHeading>Payment due</TileHeading>
        <Typography
          sx={{ fontSize: rem(12), fontWeight: 600, color: caution.ink }}
        >
          Billed to you
        </Typography>
      </Box>

      <Typography
        sx={{
          marginTop: rem(10),
          fontSize: rem(30),
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
          color: tokens.ink,
        }}
      >
        {money(payment.amount)}
      </Typography>

      <Typography
        sx={{ marginTop: rem(6), fontSize: rem(13), color: tokens.inkFaint }}
      >
        Policy {payment.reference}
      </Typography>

      <TilePrimaryButton
        component={Link}
        to={`/customer/policies/${payment.policyId}/pay/${paymentProcessor}/${payment.product}`}
        sx={{ marginTop: rem(14), width: '100%' }}
      >
        Pay premium
      </TilePrimaryButton>
    </Box>
  );
}
